import { useTranslation } from "react-i18next";

import Header from "common/components/Header/Header";
import ImportModal from "common/components/Import/ImportModal";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useList from "common/hooks/useList";
import FilteredList from "common/components/Table/FilteredList";
import useItem from "common/hooks/useItem";
import AlertsContext from "common/providers/alerts";
import {
  DocumentArrowDownIcon,
  ListBulletIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import { IoPaw } from "react-icons/io5";
import useRouter from "common/hooks/use-router";
import { getStoredData } from "common/utils/fnAsyncStorage";
import { Constants } from "constants/Constants";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import useAuth from "common/hooks/useAuth";
import usePark from "common/hooks/usePark";
import { FiBox } from "react-icons/fi";
import fetchJSON from "common/utils/fetchJSON";
import { ImportStatus } from "types/Import";
import Select from "common/components/Select/Select";
import exportData from "common/utils/exportData";
import SpecyForm from "pages/Species/SpecyForm";
import Drawer from "common/components/Drawer/Drawer";
import { useSearchParams } from "react-router-dom";
import { IconNames } from "components/Icon/Icon";
import { getParkConfiguration } from "types/Park";
import ImportItem from "./ImportItem";

function Imports() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const { user: currentUser } = useAuth();
  const { park } = usePark();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  // Code spécifique l'ajout d'une espèce depuis le sélecteur
  const [openDrawer, setOpenDrawer] = useState(false);
  const [refreshItem, setRefreshKey] = useState(false);

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<any>("imports", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    defaultSort: "createdAt:DESC",
    cachePrefix: "Imports_all",
    listenToEvents: [
      "import_file_imported",
      "import_file_new_data",
      "import_file_imported_with_error",
      "import_file_updated",
    ],
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedSpecy, setSelectedSpecy] = useState<string | null>(null);
  const [isImportButtonEnabled, setIsImportButtonEnabled] = useState(true);
  const [importType, setImportType] = useState<string>("");

  const { removeItem } = useItem<any>("imports", "");

  const parkConfiguration = useMemo(
    () => (park ? getParkConfiguration(park) : null),
    [park]
  );

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const statusList = useMemo(() => {
    const status: {
      id: string;
      name: string;
      count: number | null;
      icon: IconNames | React.ReactElement | React.ComponentType<any>;
      color: string;
    }[] = [
      {
        id: "",
        name: t("status.all"),
        count: null,
        icon: ListBulletIcon,
        color: "secondary",
      },
    ];
    if (parkConfiguration?.hasZimsImport) {
      status.push({
        id: "zims",
        name: t("import.zims"),
        count: null,
        icon: IoPaw,
        color: "primary",
      });
    }
    status.push({
      id: "observationGrid",
      name: t("forms.observationGrids"),
      count: null,
      icon: TableCellsIcon,
      color: "ternary",
    });
    if (parkConfiguration?.hasTrainings) {
      status.push({
        id: "training",
        name: t("titlePages.training-exercises"),
        count: null,
        icon: FiBox,
        color: "blue",
      });
    }
    return status;
  }, [t, parkConfiguration?.hasTrainings, parkConfiguration?.hasZimsImport]);

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "fileName$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-[2]",
      },
      {
        Header: t("common.status"),
        filterName: "status",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <Select
            options={[
              {
                value: ImportStatus.INCOMING,
                label: t("import.incoming"),
              },
              {
                value: ImportStatus.IN_PROGRESS,
                label: t("import.in_progress"),
              },
              { value: ImportStatus.COMPLETE, label: t("import.complete") },
              {
                value: ImportStatus.COMPLETE_WITH_ERRORS,
                label: t("import.completeWithErrors"),
              },
              {
                value: ImportStatus.ERROR,
                label: t("import.error"),
              },
              { value: ImportStatus.CANCELED, label: t("import.canceled") },
            ]}
            icon="StarIcon"
            {...props}
          />
        ),
      },
    ],
    [t]
  );

  const handleNavigate = (id: any) => {
    navigate(`/imports/${id}`);
  };

  const onCancelImport = useCallback(async (importItem: any) => {
    try {
      await fetchJSON({
        url: `imports/cancel`,
        method: "PUT",
        payload: {
          data: {
            ...importItem,
          },
        },
      });
    } catch (err) {
      console.error("Update import status error:", err);
    }
  }, []);

  // -- SPECIFIC CODE FOR THIS PROJECT --
  const handleImportFiles = async (data: any) => {
    const formData = new FormData();

    if (data.files && data.files.length > 0) {
      data.files.forEach((file: File) => {
        formData.append("files", file);
      });
    }

    Object.keys(data).forEach((key) => {
      if (key !== "files") {
        formData.append(key, data[key]);
      }
    });

    formData.append("importType", importType);
    if (park) {
      formData.append("park", park?.id);
    }

    // Zims Specific code
    if (selectedSpecy) {
      formData.append("specy", selectedSpecy);
    }

    try {
      const headers = new Headers();
      const token = await getStoredData("token");

      if (token) {
        headers.append("Authorization", `Bearer ${token}`);
      }

      await fetch(`${Constants.API_URL}/imports`, {
        method: "POST",
        headers,
        body: formData,
      });
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setImportType("");
      setSelectedSpecy(null);
      setIsImportButtonEnabled(false);
    }
  };

  const handleSpecyChange = (value: string) => {
    setSelectedSpecy(value);
    setIsImportButtonEnabled(!!value);
  };

  useEffect(() => {
    if (importType === "zims") {
      setIsImportButtonEnabled(!!selectedSpecy);
    } else if (importType !== "") {
      setIsImportButtonEnabled(true);
    } else {
      setIsImportButtonEnabled(false);
    }
  }, [importType, selectedSpecy]);

  const handleCancelModal = useCallback(() => {
    setImportType("");
    setSelectedSpecy(null);
    setShowImportModal(false);
  }, []);

  const { saveItem }: any = useItem("species", null, {});

  const handleSubmitSpeciesForm = useCallback(
    async (newSpeciesValue: any) => {
      try {
        const newItem = await saveItem(newSpeciesValue);
        setSelectedSpecy(newItem.id);
        setRefreshKey(newItem);
      } finally {
        setOpenDrawer(false);
        setShowImportModal(true);
      }
    },
    [saveItem]
  );

  const importModalFieldTypesFromPermissions = useMemo(() => {
    const fields = [];

    const canImportSpecies =
      (parkConfiguration?.hasZimsImport &&
        (currentUser?.isAdmin ||
          currentUser?.isSuperadmin ||
          ((currentUser?.isManager || currentUser?.isObserver) &&
            currentUser?.administrationPermissions?.imports?.species))) ??
      false;

    const canImportObservationGrids =
      (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.imports?.[
            "observation-grid"
          ])) ??
      false;

    const canImportTrainingGrids =
      (parkConfiguration?.hasTrainings &&
        (currentUser?.isAdmin ||
          currentUser?.isSuperadmin ||
          ((currentUser?.isManager || currentUser?.isObserver) &&
            currentUser?.administrationPermissions?.imports?.[
              "training-grid"
            ]))) ??
      false;

    if (canImportSpecies) {
      fields.push({ label: t("forms.animal"), value: "zims" });
    }

    if (canImportObservationGrids) {
      fields.push({
        label: t("history.modelName.observation-grid"),
        value: "observationGrid",
      });
    }

    if (canImportTrainingGrids) {
      fields.push({
        label: t("administrationPermissions.training-grid.training-grid"),
        value: "training",
      });
    }

    return fields;
  }, [
    t,
    currentUser,
    parkConfiguration?.hasTrainings,
    parkConfiguration?.hasZimsImport,
  ]);

  // -----------------------------------

  useEffect(() => {
    if (type) {
      setFilter("importType", type);
      setImportType(type);
    }
  }, [setFilter, type]);

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.imports")}
        subtitle={t("titleLegends.import")}
        btnTitle={t("newItem.import")}
        btnSubtitle={t("newItem.importLegend")}
        Icon={DocumentArrowDownIcon}
        noAdd={!importModalFieldTypesFromPermissions?.length}
        addLinkDisabled={!importModalFieldTypesFromPermissions?.length}
        filters={filters}
        setFilter={setFilter}
        isBeforeFilters
        isCustomButton
        onClickCustomButton={() => setShowImportModal(true)}
        filterName="importType"
        FilterComponent={(props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        )}
      />
      <FilteredList
        data={items ?? []}
        columns={columns}
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={(props: any) => (
          <ImportItem onCancelImport={onCancelImport} {...props} />
        )}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
      {showImportModal && ImportModal && (
        <ImportModal
          onImport={handleImportFiles}
          onClose={handleCancelModal}
          onDownloadExample={
            importType && importType !== "zims"
              ? () =>
                  exportData({
                    importType,
                    isTemplate: true,
                    lang: currentUser?.language,
                  })
              : undefined
          }
          isOpen={showImportModal}
          fieldTypes={importModalFieldTypesFromPermissions}
          enableSubmit={isImportButtonEnabled}
          setSelectedType={setImportType}
          selectedType={importType}
        >
          {importType === "zims" && (
            <div className="flex flex-col gap-2 items-start">
              <span className="text-m font-semibold">
                {t("forms.speciesSelect")}
              </span>
              <SpecySelect
                onChange={handleSpecyChange}
                enableAddNewEntry
                onAddNewData={() => {
                  setOpenDrawer(true);
                  setShowImportModal(false);
                }}
                refreshItem={refreshItem}
              />
            </div>
          )}
        </ImportModal>
      )}
      <Drawer
        isOpen={openDrawer}
        onClose={() => setOpenDrawer(false)}
        anchor="right"
      >
        <SpecyForm
          onSubmit={(value: any) => handleSubmitSpeciesForm(value)}
          onCancel={() => setOpenDrawer(false)}
          additionalHeaderStyle="ml-12"
        />
      </Drawer>
    </div>
  );
}

export default Imports;
